<template>
  <m-modal v-if="options" :title="options.title" @close="handleClose(false)">
    <m-form @submit="handlePromptSubmit">
      <!-- isHtml messages can interpolate user-entered data (subject names, addresses), so sanitize before -->
      <!-- rendering. Matches the MSanitizedHtml pattern; the eslint warning is safe because of the sanitize. -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="options.isHtml" v-html="sanitizedMessage"></div>
      <p v-else class="whitespace-pre-line">{{ options.message }}</p>
      <input v-if="isPrompt" ref="inputEl" v-model="inputValue" type="text" class="input input-bordered mt-3 w-full" />
      <template #actions>
        <m-button v-if="showCancel" theme="link" @click="handleClose(false)">{{ cancelText }}</m-button>
        <m-button :disabled="isPrompt && !inputValue.trim()" @click="handleClose(true)">{{ confirmText }}</m-button>
      </template>
    </m-form>
  </m-modal>
</template>
<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';
  import DOMPurify from 'dompurify';
  import MModal from '@shared/components/MModal/MModal.vue';
  import MForm from '@core/components/MForm/MForm.vue';
  import MButton from '@core/components/MButton/MButton.vue';
  import { EVENTS, globalEventBus } from './eventBus';
  import type { PrivatePromptOptions } from './interfaces';

  const options = ref<PrivatePromptOptions | null>(null);
  const inputValue = ref('');
  const inputEl = ref<HTMLInputElement | null>(null);

  const isPrompt = computed(() => options.value?.type === 'prompt');
  // isHtml messages may contain user-entered data — sanitize so injected markup/scripts can't run.
  const sanitizedMessage = computed(() => DOMPurify.sanitize(options.value?.message ?? ''));
  // Both confirm and prompt are dismissable; only alert forces a single acknowledgement.
  const showCancel = computed(() => options.value?.type === 'confirm' || options.value?.type === 'prompt');
  const cancelText = computed(() => options.value?.cancelText || 'Cancel');
  const confirmText = computed(() => options.value?.confirmText || 'Confirm');

  const handleClose = (confirmed: boolean) => {
    // prompt resolves to the typed string (empty on cancel); confirm/alert resolve to a boolean. These
    // are distinct events because the two callers await different result types.
    if (options.value?.type === 'prompt') {
      globalEventBus.emit(EVENTS.FINISH_PROMPT, { result: confirmed ? inputValue.value.trim() : '' });
    } else {
      globalEventBus.emit(EVENTS.FINISH_CONFIRM, { result: confirmed });
    }
    options.value = null;
    inputValue.value = '';
  };
  // Enter submits only a non-empty prompt; confirm/alert keep their prior behavior (Enter does nothing).
  const handlePromptSubmit = () => {
    if (isPrompt.value && inputValue.value.trim()) handleClose(true);
  };
  globalEventBus.on(EVENTS.PROMPT, (opts) => {
    inputValue.value = '';
    options.value = opts;
    if (opts.type === 'prompt') nextTick(() => inputEl.value?.focus());
  });
</script>
<style scoped>
  .btn.btn-primary,
  .btn.btn-accent {
    @apply rounded normal-case;
  }

  button.link {
    @apply font-bold underline underline-offset-4;
  }

  /* Styles for HTML content */
  :deep(div[v-html]) {
    line-height: 1.5;
  }

  :deep(div[v-html] p) {
    margin-bottom: 1rem;
  }

  :deep(div[v-html] ul, div[v-html] ol) {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }

  :deep(div[v-html] strong) {
    font-weight: 600;
  }
</style>
